import React, { useCallback } from 'react';
import { ReactComponent as Lab } from 'assets/icons/testKits/testKit.svg';
import { ReactComponent as FileDownloadIcon } from 'assets/icons/file-download.svg';

import { Card } from '../Card';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import ButtonComponent from 'components/button/buttonComponent';
import {
  GetFhirAppointmentDiagnosticReportOutput,
  Hasura_Test_Types_Enum,
} from 'graphql/generated/hasura';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds } from 'utilities/constants';
import { openPdfOnMobileSafe } from 'utilities/fileHelpers';
import moment from 'moment/moment';
import { useNavigate } from 'react-router-dom';
import {
  HORMONE_QUESTIONNAIRE_RESULT,
  MICROBIOME_QUESTIONNAIRE_RESULT,
  TEST_KITS_MANAGEMENT,
} from 'utilities/routes';

interface TestKitsProps {
  diagnosticReports: GetFhirAppointmentDiagnosticReportOutput[];
  patientId?: string;
  showQuestionnaireButton?: boolean;
  showResultButton?: boolean;
}

const TestKits: React.FC<TestKitsProps> = ({
  diagnosticReports = [],
  patientId,
  showQuestionnaireButton = false,
  showResultButton = false,
}) => {
  const { fetchFileFromStorage } = useGetFileFromStorage();
  const navigate = useNavigate();
  const getDownloadFunction = useCallback(
    async (diagnosticReportId: string) => {
      if (!diagnosticReportId) return;
      const pdfLink = await fetchFileFromStorage(diagnosticReportId, patientId);
      openPdfOnMobileSafe(pdfLink);
    },
    [fetchFileFromStorage, patientId],
  );

  const { data: locale } = useGetComponent({
    componentId: componentIds.TEST_KIT_LABELS,
    locale: 'en',
  });

  const checkShowQuestionnaireButton = (type: Hasura_Test_Types_Enum) =>
    showQuestionnaireButton &&
    [
      Hasura_Test_Types_Enum.ZrtHeavyMetals,
      Hasura_Test_Types_Enum.ZrtHormone,
      Hasura_Test_Types_Enum.ZrtNeurotransmitters,
      Hasura_Test_Types_Enum.ZrtInflammatory,
      Hasura_Test_Types_Enum.DsGiMap,
    ].includes(type);

  const handleClickQuestionnaire = (
    diagnosticReport: GetFhirAppointmentDiagnosticReportOutput,
  ) => {
    const route =
      diagnosticReport.diagnosticReportTestType !==
      Hasura_Test_Types_Enum.DsGiMap
        ? `${TEST_KITS_MANAGEMENT}${HORMONE_QUESTIONNAIRE_RESULT}`
        : `${TEST_KITS_MANAGEMENT}${MICROBIOME_QUESTIONNAIRE_RESULT}`;
    navigate(route, {
      state: {
        patientId: patientId,
        testKitData: {
          testType: diagnosticReport.diagnosticReportTestType,
          testKitIdNumber: diagnosticReport.swabCode,
        },
      },
    });
  };

  if (!diagnosticReports.length) {
    return (
      <Card
        title={'Diagnostic test kit results'}
        titleClassName="flex text-h4 text-btn-black font-semibold"
        backgroundColor="bg-white desktop:!gap-30"
        defaultExpanded={true}
        showSeparator
        Icon={() => <Lab className="w-6 h-6 fill-btn-black" />}
      >
        <p className="text-med-gray-3 font-medium py-4">
          There are no test kits linked to this appointment
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-2.5 w-full">
      <Card
        title={'Diagnostic test kit results'}
        titleClassName="flex text-h4 text-btn-black font-semibold"
        backgroundColor="bg-white desktop:!gap-30"
        showSeparator
        defaultExpanded={true}
        Icon={() => <Lab className="w-6 h-6 fill-btn-black" />}
      >
        <div className="w-full max-h-[300px] overflow-y-auto flex flex-col gap-2.5">
          {diagnosticReports.map((diagnosticReport) => {
            const {
              providerPdfId,
              patientPdfId,
              patientPdf,
              providerPdf,
              diagnosticReportTestType,
              createdAt,
            } = diagnosticReport;

            const creation =
              patientPdf?.creation || providerPdf?.creation || createdAt;

            const showQuestionnaireButton = checkShowQuestionnaireButton(
              diagnosticReportTestType as Hasura_Test_Types_Enum,
            );

            return (
              <div className="flex flex-col desktop:flex-row justify-between border border-gray-opacity-15 rounded-lg desktop:items-center w-full gap-2.5 py-2.5 px-2.5 pl-5">
                <div className="flex items-center gap-2">
                  <div className="text-med-gray-3">
                    <Lab className={'w-6 h-6'} />
                  </div>
                  <div className="flex items-center text-btn-black font-semibold">
                    {locale?.[diagnosticReportTestType] ??
                      diagnosticReportTestType}
                  </div>
                  <p className="text-sm text-med-gray-3 font-medium">
                    {moment(creation || undefined).format('DD/MM/YYYY')}
                  </p>
                </div>

                <div className="flex items-center gap-2.5">
                  {showResultButton && (
                    <ButtonComponent
                      onClick={() =>
                        getDownloadFunction(
                          (providerPdfId || patientPdfId) as string,
                        )
                      }
                      type={'outlined'}
                      testID={'download-file-button'}
                      Icon={FileDownloadIcon}
                      iconHeight="w-[15px] h-[18px] cursor-pointer fill-current"
                      iconPosition="left"
                      className="!border items-center gap-2.5 !text-xs !w-auto"
                    >
                      <span>Result</span>
                    </ButtonComponent>
                  )}

                  {showQuestionnaireButton && (
                    <ButtonComponent
                      onClick={() => handleClickQuestionnaire(diagnosticReport)}
                      type={'outlined'}
                      testID={'download-file-button'}
                      Icon={FileDownloadIcon}
                      iconHeight="w-[15px] h-[18px] cursor-pointer fill-current"
                      iconPosition="left"
                      className="flex !border items-center gap-2.5 !text-xs !w-auto"
                    >
                      <span>Questionnaire</span>
                    </ButtonComponent>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default TestKits;
